import {
  getHash,
  getCountry,
  getSafeAllowedPaymentTypes,
} from '@tectonics/prs/incoming-payment';
import type { OpenedIncomingPayment } from '@tectonics/prs/incoming-payment';
import type { Country } from '@tectonics/prs/country';
import type { LookAndFeel } from '@tectonics/look-and-feel';
import type { UserInputValues } from '@tectonics/prs/user-input-values';
import type { UserInputValidationErrors } from '@tectonics/prs/validation';
import { normalizeColombianPhoneNumber } from '@tectonics/commons/country/countries/colombia';

type CheckoutFailProps = {
  readonly incomingPayment: OpenedIncomingPayment;
  readonly lookAndFeel: LookAndFeel;
  readonly userInputValues: UserInputValues;
  readonly userInputValidationResult: UserInputValidationErrors;
};

type SensitiveUserInputField =
  | 'cardHolderName'
  | 'cardNumber'
  | 'cardExpiryDate'
  | 'cardSecurityCode';

export interface CheckoutFailPayload {
  readonly type: 'checkout_fail';
  readonly project: 'tectonics';
  readonly payload: {
    readonly user_input_data: Omit<UserInputValues, SensitiveUserInputField>;
    readonly user_input_validation: Record<string, string>;
    readonly payment_data: {
      readonly hash: string;
      readonly country: Country;
      readonly payment_options: ReadonlyArray<string>;
      readonly look_and_feel_code: string;
    };
  };
}

export class CheckoutFail {
  readonly props: CheckoutFailProps;

  constructor(props: CheckoutFailProps) {
    this.props = props;
  }

  asObject(): CheckoutFailPayload {
    const { incomingPayment, lookAndFeel, userInputValues, userInputValidationResult } = this.props;
    return {
      type: 'checkout_fail',
      project: 'tectonics',
      payload: {
        user_input_data: filterUserInputValues(userInputValues),
        user_input_validation: extractUserInputValidationErrorForAnalytics(userInputValidationResult),
        payment_data: {
          ...filterIncomingPayment(incomingPayment),
          look_and_feel_code: lookAndFeel.code,
        },
      },
    };
  }

  asJSON(): string {
    return JSON.stringify(this.asObject());
  }
}

function filterUserInputValues(
  userInputValues: UserInputValues
): Omit<UserInputValues, SensitiveUserInputField> {
  const filtered: Record<string, unknown> = { ...userInputValues };
  delete filtered['cardHolderName'];
  delete filtered['cardNumber'];
  delete filtered['cardExpiryDate'];
  delete filtered['cardSecurityCode'];
  if (filtered['colombianPhoneNumber']) {
    filtered['colombianPhoneNumber'] = normalizeColombianPhoneNumber(
      filtered['colombianPhoneNumber'] as string
    );
  }
  return filtered;
}

function filterIncomingPayment(incomingPayment: OpenedIncomingPayment): {
  readonly hash: string;
  readonly country: Country;
  readonly payment_options: ReadonlyArray<string>;
} {
  return {
    hash: getHash(incomingPayment),
    country: getCountry(incomingPayment),
    payment_options: getSafeAllowedPaymentTypes(incomingPayment),
  };
}

function extractUserInputValidationErrorForAnalytics(
  userInputValidation: UserInputValidationErrors
): Record<string, string> {
  const userInputValidationForAnalytics: Record<string, string> = {};
  Object.entries(userInputValidation).forEach(([name, validationError]) => {
    if (validationError) {
      userInputValidationForAnalytics[name] = validationError.id;
    }
  });
  return userInputValidationForAnalytics;
}
